import React, { useState, useRef, useEffect } from 'react';
import rmSlide1 from '../../assets/RiddleMe/1.webp';
import rmSlide2 from '../../assets/RiddleMe/2.webp';
import rmSlide3 from '../../assets/RiddleMe/3.webp';
import rmSlide4 from '../../assets/RiddleMe/4.webp';
import rmSlide5 from '../../assets/RiddleMe/5.webp';
import rmSlide6 from '../../assets/RiddleMe/6.webp';
import rmSlide7 from '../../assets/RiddleMe/7.webp';
import ddhappsIcon from '../../assets/DDHAPPS/ourLogos/ddhapps_logo _offical.png';
import { MONO, SANS } from '../../constants/fonts';

const RIDDLE_ME_SLIDES = [rmSlide1, rmSlide2, rmSlide3, rmSlide4, rmSlide5, rmSlide6, rmSlide7];

// ─── App catalogue ────────────────────────────────────────────────────────────
const APPS = [
  {
    id: 'riddleme',
    title: 'RiddleMe',
    tagline: 'A new riddle,\nevery day.',
    shortDesc: 'Daily brain-teasing puzzles.',
    description:
      'Challenge your mind with a fresh riddle every day. Simple to pick up, hard to put down — and deeply satisfying when you crack it.',
    version: '1.0.9',
    accentColor: '#a78bfa',
    iconSrc: '/assets-in-site/icon.png',
    iconGradient: null,
    slides: RIDDLE_ME_SLIDES,
    iosUrl:     'https://apps.apple.com/gb/app/riddleme-the-daily-riddle/id6757712671',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.riddleme.app.v2',
  },
  {
    id: 'ddhapps',
    title: 'DDHAPPS',
    shortDesc: 'Coming soon.',
    comingSoon: true,
    accentColor: '#6b7280',
    iconSrc: ddhappsIcon,
    iconGradient: null,
  },
  {
    id: 'yours',
    title: 'Your app?',
    shortDesc: 'Could be next.',
    comingSoon: true,
    accentColor: '#6366f1',
    iconGradient: 'linear-gradient(135deg, #312e81 0%, #6366f1 100%)',
  },
];

// ─── Icons ───────────────────────────────────────────────────────────────────
function AppleIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"
         aria-hidden="true" style={{ marginRight: 7, flexShrink: 0 }}>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"
         aria-hidden="true" style={{ marginRight: 7, flexShrink: 0 }}>
      <path d="M3.18 23.49c-.35-.55-.18-1.19-.18-1.82V2.34C3 1.7 2.83 1.06 3.18.5l9.6 11.5-9.6 11.49zM20.35 11l-2.72-1.57L14 12l3.63 2.57L20.35 13c.95-.55.95-1.45 0-2zM4.34 0l10.4 6-2.28 2.72L4.34 0zM4.34 24l8.12-8.72L14.74 18 4.34 24z"/>
    </svg>
  );
}

// ─── Phone slideshow ─────────────────────────────────────────────────────────
function PhoneSlideshow({ slides }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(i => (i + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {slides.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`Screenshot ${i + 1}`}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: i === current ? 1 : 0,
            transition: 'opacity 0.8s ease',
          }}
        />
      ))}
    </div>
  );
}

// ─── AppsShowcase ─────────────────────────────────────────────────────────────
function AppsShowcase({ appsRef }) {
  const [selectedId, setSelectedId] = useState(APPS[0].id);
  const [showcaseVisible, setShowcaseVisible] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const tilesRowRef = useRef(null);
  const pendingId = useRef(null);

  const handleSelect = (id) => {
    if (id === selectedId) return;
    pendingId.current = id;
    setShowcaseVisible(false);
    setTimeout(() => {
      setSelectedId(pendingId.current);
      setShowcaseVisible(true);
    }, 220);
  };

  const selectedApp  = APPS.find(a => a.id === selectedId);
  const showTeaser   = Boolean(selectedApp?.comingSoon);
  // Always render the full showcase with a real app so height never collapses
  const showcaseApp  = showTeaser ? APPS.find(a => !a.comingSoon) : selectedApp;

  return (
    <>
      <style>{`
        .store-btn:hover  { opacity: 0.7 !important; }
        .tiles-dots { display: none; }
        @media (max-width: 600px) {
          .tiles-row { flex-wrap: nowrap !important; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding-top: 6px; margin-top: -6px; padding-bottom: 4px; padding-left: 24px; padding-right: 24px; margin-left: -24px; margin-right: -24px; margin-bottom: 0 !important; }
          .tiles-row::-webkit-scrollbar { display: none; }
          .tiles-dots { display: flex; align-items: center; gap: 6px; margin-top: 12px; margin-bottom: 32px; justify-content: center; }
        }
        .app-tile {
          cursor: pointer;
          transition: border-color 0.2s ease, background 0.2s ease, transform 0.18s ease;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        .app-tile:hover { transform: translateY(-3px); }
      `}</style>

      <section ref={appsRef} style={s.appsSection}>
        <div style={s.appsSectionInner}>

          <p style={s.sectionLabel}>Our apps</p>

          {/* ── Tile picker ── */}
          <div ref={tilesRowRef} className="tiles-row" style={s.tilesRow}>
            {APPS.map(app => {
              const isActive = app.id === selectedId;
              return (
                <div
                  key={app.id}
                  className="app-tile"
                  role="button"
                  tabIndex={0}
                  aria-pressed={isActive}
                  onClick={() => handleSelect(app.id)}
                  onKeyDown={e => e.key === 'Enter' && handleSelect(app.id)}
                  onMouseEnter={() => setHoveredId(app.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    ...s.tile,
                    ...(isActive ? {
                      background: `${app.accentColor}12`,
                      border: `1px ${app.comingSoon ? 'dashed' : 'solid'} ${app.accentColor}72`,
                    } : {}),
                    ...(app.comingSoon && !isActive ? s.tileComingSoon : {}),
                    ...(hoveredId === app.id && !isActive ? {
                      border: `1px ${app.comingSoon ? 'dashed' : 'solid'} ${app.accentColor}58`,
                    } : {}),
                  }}
                >
                  {/* App icon */}
                  <div style={{
                    ...s.tileIcon,
                    background: app.iconGradient || 'transparent',
                    ...(app.comingSoon && !app.iconSrc ? s.tileIconMuted : {}),
                  }}>
                    {app.iconSrc
                      ? <img src={app.iconSrc} alt={`${app.title} icon`} style={s.tileIconImg} />
                      : <span style={s.tileIconInitial}>{app.comingSoon ? '?' : app.title.charAt(0)}</span>}
                  </div>
                  <span style={{ ...s.tileName, ...(isActive ? s.tileNameActive : {}) }}>
                    {app.title}
                  </span>
                  <span style={s.tileShortDesc}>{app.shortDesc}</span>
                  {app.comingSoon && <span style={s.tileComingSoonBadge}>In development</span>}
                  {isActive && <span style={{
                    ...s.tileActiveDot,
                    background: app.accentColor,
                    boxShadow: `0 0 6px ${app.accentColor}b0`,
                  }} />}
                </div>
              );
            })}
          </div>
          <div className="tiles-dots">
            {APPS.map((app, i) => {
              const active = app.id === selectedId;
              return (
                <div key={i} style={{
                  width: active ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: active ? '#fff' : 'rgba(255,255,255,0.25)',
                  transition: 'width 0.3s ease, background 0.3s ease',
                }} />
              );
            })}
          </div>

          {/* ── Showcase panels — grid-stacked so height never jumps ── */}
          <div style={s.showcaseContainer}>

            {/* Full showcase — always in the DOM; drives the container height */}
            <div style={{
              ...s.showcaseLayer,
              opacity:       !showTeaser && showcaseVisible ? 1 : 0,
              transform:     !showTeaser && showcaseVisible ? 'translateY(0)' : 'translateY(14px)',
              pointerEvents: !showTeaser ? 'auto' : 'none',
              transition:    'opacity 0.22s ease, transform 0.22s ease',
            }}>
              {showcaseApp && (
                <div style={s.appShowcase}>

                  {/* Left: info */}
                  <div style={s.showcaseInfo}>
                    <div style={s.showcaseIconRow}>
                      <div style={{
                        ...s.showcaseIcon,
                        background: showcaseApp.iconGradient || 'transparent',
                        ...(showcaseApp.iconSrc ? { padding: 0 } : {}),
                      }}>
                        {showcaseApp.iconSrc
                          ? <img src={showcaseApp.iconSrc} alt={`${showcaseApp.title} icon`}
                                 style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit', display: 'block' }} />
                          : <span style={s.showcaseIconInitial}>{showcaseApp.title.charAt(0)}</span>}
                      </div>
                      <span style={{ ...s.appChip, color: showcaseApp.accentColor }}>{showcaseApp.title}</span>
                    </div>
                    <h2 style={s.showcaseHeadline}>
                      {showcaseApp.tagline.split('\n').map((line, i, arr) => (
                        <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                      ))}
                    </h2>
                    <p style={s.showcaseDesc}>{showcaseApp.description}</p>
                    <div style={s.storeLinks}>
                      <a href={showcaseApp.iosUrl} target="_blank" rel="noreferrer"
                         className="store-btn" style={s.storeBtn}>
                        <AppleIcon /> App Store
                      </a>
                      <a href={showcaseApp.androidUrl} target="_blank" rel="noreferrer"
                         className="store-btn" style={s.storeBtn}>
                        <PlayIcon /> Google Play
                      </a>
                    </div>
                    <div style={s.versionRow}>
                      <span style={s.versionLabel}>Version {showcaseApp.version}</span>
                      <span style={s.liveStatus}>
                        <span style={s.liveDot} />
                        <span style={s.liveLabel}>Live</span>
                      </span>
                    </div>
                  </div>

                  {/* Right: phone mockup */}
                  <div style={s.showcasePhoneWrap}>
                    <div style={s.phoneGlow} aria-hidden="true" />
                    <div style={s.phone}>
                      <div style={s.phoneIsland} aria-hidden="true" />
                      <div style={s.phoneScreen}>
                        {showcaseApp.slides
                          ? <PhoneSlideshow slides={showcaseApp.slides} />
                          : (
                            <div style={s.gifPlaceholder}>
                              <span style={s.gifPlaceholderTitle}>{showcaseApp.title}</span>
                              <span style={s.gifPlaceholderSub}>preview coming soon</span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>

            {/* Teaser — same split layout as the showcase so heights match */}
            <div style={{
              ...s.teaserLayer,
              opacity:       showTeaser && showcaseVisible ? 1 : 0,
              transform:     showTeaser && showcaseVisible ? 'translateY(0)' : 'translateY(14px)',
              pointerEvents: showTeaser ? 'auto' : 'none',
              transition:    'opacity 0.22s ease, transform 0.22s ease',
            }}>
              <div style={s.appShowcase}>

                {/* Left: info */}
                <div style={s.showcaseInfo}>
                  <div style={s.showcaseIconRow}>
                    <div style={{
                      ...s.showcaseIcon,
                      background: selectedApp?.iconSrc ? 'transparent' : selectedApp?.iconGradient,
                      padding: selectedApp?.iconSrc ? 0 : undefined,
                    }}>
                      {selectedApp?.iconSrc
                        ? <img src={selectedApp.iconSrc} alt={`${selectedApp.title} icon`}
                               style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit', display: 'block' }} />
                        : <span style={s.showcaseIconInitial}>?</span>}
                    </div>
                    <span style={{ ...s.appChip, color: selectedApp?.accentColor }}>{selectedApp?.title}</span>
                  </div>
                  {selectedApp?.id === 'yours' ? (
                    <>
                      <h2 style={s.showcaseHeadline}>
                        This could be<br />your next app.
                      </h2>
                      <p style={{ ...s.showcaseDesc, marginBottom: 24 }}>
                        Got an idea? We'd love to hear it. We build apps people love. Yours could be next.
                      </p>
                      <a href="/contact" style={{
                        display: 'inline-block',
                        padding: '12px 24px',
                        background: '#6366f1',
                        color: '#fff',
                        borderRadius: 10,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        textDecoration: 'none',
                        width: 'fit-content',
                      }}>Let's talk</a>
                    </>
                  ) : (
                    <>
                      <h2 style={s.showcaseHeadline}>
                        Something new<br />is brewing.
                      </h2>
                      <div style={s.teaserStatus}>
                        <span style={s.teaserDot} />
                        <span style={s.teaserStatusText}>In development</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Right: phone with a mysterious dark screen */}
                <div style={s.showcasePhoneWrap}>
                  <div style={{ ...s.phoneGlow, background: `radial-gradient(ellipse at 50% 100%, ${selectedApp?.id === 'yours' ? 'rgba(99,102,241,0.2)' : 'rgba(107,114,128,0.18)'} 0%, transparent 70%)` }} aria-hidden="true" />
                  <div style={s.phone}>
                    <div style={s.phoneIsland} aria-hidden="true" />
                    <div style={s.phoneScreen}>
                      <div style={s.teaserScreen}>
                        <div style={{
                          ...s.teaserScreenGlow,
                          background: selectedApp?.id === 'yours'
                            ? 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)'
                            : 'radial-gradient(circle, rgba(107,114,128,0.25) 0%, transparent 70%)',
                        }} />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = {
  // ── Apps section
  appsSection: {
    background: '#0f0f13',
    padding: 'clamp(56px, 8vw, 96px) 24px clamp(64px, 8vw, 104px)',
  },
  appsSectionInner: {
    maxWidth: 960,
    margin: '0 auto',
  },
  sectionLabel: {
    fontFamily: MONO,
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#9ca3af',
    margin: '0 0 28px',
  },

  // ── Tiles
  tilesRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 'clamp(24px, 6vw, 52px)',  /* overridden to 0 on mobile via CSS */
  },
  tile: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexShrink: 0,
    gap: 8,
    padding: '16px 18px 18px',
    background: '#16161c',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 16,
    width: 158,
    boxSizing: 'border-box',
  },
  tileActive: {
    background: 'rgba(167,139,250,0.07)',
    border: '1px solid rgba(167,139,250,0.45)',
  },
  tileIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  tileIconImg: { width: '100%', height: '100%', objectFit: 'cover' },
  tileIconInitial: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '1.4rem',
    fontFamily: SANS,
  },
  tileName: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#8b909a',
    letterSpacing: '0.01em',
  },
  tileNameActive: { color: '#d8dae8' },
  tileShortDesc: {
    fontSize: '0.72rem',
    color: '#6b7280',
    lineHeight: 1.45,
  },
  tileActiveDot: {
    position: 'absolute',
    bottom: 10,
    right: 12,
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#a78bfa',
    boxShadow: '0 0 6px rgba(167,139,250,0.7)',
  },
  tileComingSoon: {
    opacity: 0.55,
    border: '1px dashed rgba(255,255,255,0.1)',
  },
  tileIconMuted: {
    filter: 'saturate(0.4) brightness(0.7)',
  },
  tileComingSoonBadge: {
    fontFamily: MONO,
    fontSize: '0.62rem',
    letterSpacing: '0.08em',
    color: '#3a3d4a',
    textTransform: 'uppercase',
  },

  teaserStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  teaserDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#6b7280',
    boxShadow: '0 0 8px rgba(107,114,128,0.6)',
    flexShrink: 0,
  },
  teaserStatusText: {
    fontFamily: MONO,
    fontSize: '0.72rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#9ca3af',
  },
  // Mysterious phone screen for the teaser
  teaserScreen: {
    width: '100%',
    height: '100%',
    background: '#0a080a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  teaserScreenGlow: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(107,114,128,0.25) 0%, transparent 70%)',
    filter: 'blur(12px)',
  },

  // ── Showcase container — grid stacks both layers so height never changes
  showcaseContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr',
  },
  // Both layers sit in the same grid cell and overlap
  showcaseLayer: {
    gridArea: '1 / 1',
    willChange: 'opacity, transform',
  },
  teaserLayer: {
    gridArea: '1 / 1',
    willChange: 'opacity, transform',
  },

  // Showcase row — text left, phone right
  appShowcase: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'clamp(40px, 8vw, 96px)',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },

  // Info column
  showcaseInfo: {
    flex: '1 1 280px',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    paddingTop: 'clamp(6px, 2.5vw, 48px)',
  },
  showcaseIconRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  showcaseIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
  },
  showcaseIconInitial: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '1.6rem',
    fontFamily: SANS,
  },
  appChip: {
    fontFamily: SANS,
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '0.01em',
    color: '#a78bfa',
  },
  showcaseHeadline: {
    fontSize: 'clamp(2rem, 5vw, 3.2rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.12,
    color: '#f0f0f5',
    margin: 0,
  },
  showcaseDesc: {
    fontSize: '0.92rem',
    color: '#6b7080',
    lineHeight: 1.75,
    margin: 0,
  },
  storeLinks: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  storeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#d8dae8',
    textDecoration: 'none',
    fontSize: '0.8rem',
    fontWeight: 600,
    padding: '10px 18px',
    borderRadius: 10,
    letterSpacing: '0.01em',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    transition: 'opacity 0.15s',
  },
  versionLabel: {
    fontFamily: MONO,
    fontSize: '0.68rem',
    color: '#4a4f66',
    letterSpacing: '0.06em',
  },
  versionRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  },
  liveStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 8px rgba(34,197,94,0.6)',
    flexShrink: 0,
  },
  liveLabel: {
    fontFamily: MONO,
    fontSize: '0.68rem',
    color: '#4ade80',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  // Phone column
  showcasePhoneWrap: {
    flex: '0 0 auto',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0 40px',
  },
  phoneGlow: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 280,
    height: 180,
    background: 'radial-gradient(ellipse at 50% 100%, rgba(167,139,250,0.22) 0%, transparent 70%)',
    filter: 'blur(16px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  phone: {
    position: 'relative',
    zIndex: 1,
    width: 'clamp(200px, 30vw, 248px)',
    borderRadius: 48,
    background: '#18181c',
    border: '8px solid #242428',
    boxShadow: '0 0 0 1px #2e2e34, 0 48px 96px rgba(0,0,0,0.55), 0 16px 32px rgba(0,0,0,0.35)',
    overflow: 'hidden',
  },
  phoneIsland: {
    position: 'absolute',
    top: 11,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 76,
    height: 22,
    background: '#000',
    borderRadius: 11,
    zIndex: 3,
  },
  phoneScreen: {
    width: '100%',
    aspectRatio: '9 / 19.5',
    background: '#0a0a0e',
    overflow: 'hidden',
    position: 'relative',
  },
  gifPlaceholder: {
    width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 8, background: '#0d0d12',
  },
  gifPlaceholderTitle: {
    color: '#2a2a38', fontSize: '0.82rem', fontWeight: 600,
    fontFamily: MONO, letterSpacing: '0.04em',
  },
  gifPlaceholderSub: { color: '#1e1e28', fontSize: '0.65rem', fontFamily: MONO },
};

export default AppsShowcase;
